import { Interactive, Tooltip } from '@highlight/ui'
import type { ProcessPerformanceSample } from '@shared/performance-monitor'
import { twMerge } from 'tailwind-merge'
import { Metric } from '@renderer/features/performance-monitor/metric-chart'
import {
  COLUMN_TOOLTIPS,
  MAX_PROCESS_ROWS,
  THRESHOLDS,
  TOOLTIP_CLASS,
  sortProcesses,
  type PerformanceTotals,
  type SortColumn,
  type SortState,
} from '@renderer/features/performance-monitor/model'

interface HeaderCellProps {
  column: SortColumn
  label: string
  sort: SortState
  onSort: (column: SortColumn) => void
  alignRight?: boolean
}

function HeaderCell({ column, label, sort, onSort, alignRight }: HeaderCellProps) {
  const active = sort.column === column
  return (
    <Interactive>
      <Tooltip>
        {/* The cell-wide wrapper carries the alignment; the button shrink-wraps its label. */}
        <Tooltip.Trigger render={<div className={twMerge('flex', alignRight && 'justify-end')} />}>
          <button
            type="button"
            onClick={() => onSort(column)}
            className={twMerge(
              'pointer-events-auto cursor-pointer bg-transparent p-0 font-mono text-[10px]',
              alignRight ? 'text-right' : 'text-left',
              active ? 'text-foreground' : 'text-foreground-placeholder hover:text-foreground-secondary',
            )}
          >
            {label}
            {active && (sort.descending ? '▾' : '▴')}
          </button>
        </Tooltip.Trigger>
        <Tooltip.Content className={TOOLTIP_CLASS}>{COLUMN_TOOLTIPS[column]}</Tooltip.Content>
      </Tooltip>
    </Interactive>
  )
}

interface PerformanceTotalsProps {
  totals: PerformanceTotals
}

export function PerformanceTotals({ totals }: PerformanceTotalsProps) {
  return (
    <div className="border-t p-2.5">
      <div className="pointer-events-auto flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Tooltip.Provider>
          <Tooltip>
            <Tooltip.Trigger render={<span className="text-foreground text-[11px] font-bold" />}>
              <Metric value={totals.cpuPercent} thresholds={THRESHOLDS.cpuPercent} />
              <span className="text-foreground-placeholder">% cpu</span>
            </Tooltip.Trigger>
            <Tooltip.Content className={TOOLTIP_CLASS}>{COLUMN_TOOLTIPS.cpuPercent}</Tooltip.Content>
          </Tooltip>
          <Tooltip>
            <Tooltip.Trigger render={<span className="text-foreground text-[11px] font-bold" />}>
              <Metric value={totals.memoryMB} thresholds={THRESHOLDS.memoryMB} digits={0} />
              <span className="text-foreground-placeholder"> mb</span>
            </Tooltip.Trigger>
            <Tooltip.Content className={TOOLTIP_CLASS}>{COLUMN_TOOLTIPS.memoryMB}</Tooltip.Content>
          </Tooltip>
          <Tooltip>
            <Tooltip.Trigger render={<span className="text-foreground text-[11px] font-bold" />}>
              <Metric value={totals.idleWakeupsPerSecond} thresholds={THRESHOLDS.wakeups} digits={0} />
              <span className="text-foreground-placeholder"> wake/s</span>
            </Tooltip.Trigger>
            <Tooltip.Content className={TOOLTIP_CLASS}>{COLUMN_TOOLTIPS.idleWakeupsPerSecond}</Tooltip.Content>
          </Tooltip>
          <Tooltip>
            <Tooltip.Trigger render={<span className="text-foreground text-[11px] font-bold" />}>
              <Metric value={totals.energyImpact} thresholds={THRESHOLDS.energy} digits={0} />
              <span className="text-foreground-placeholder"> nrg</span>
            </Tooltip.Trigger>
            <Tooltip.Content className={TOOLTIP_CLASS}>{COLUMN_TOOLTIPS.energyImpact}</Tooltip.Content>
          </Tooltip>
          {totals.powerMilliwatts !== undefined && (
            <Tooltip>
              <Tooltip.Trigger render={<span className="text-foreground text-[11px] font-bold" />}>
                <Metric value={totals.powerMilliwatts} thresholds={THRESHOLDS.powerMw} digits={0} />
                <span className="text-foreground-placeholder"> mW</span>
              </Tooltip.Trigger>
              <Tooltip.Content className={TOOLTIP_CLASS}>{COLUMN_TOOLTIPS.powerMilliwatts}</Tooltip.Content>
            </Tooltip>
          )}
        </Tooltip.Provider>
      </div>
    </div>
  )
}

interface ProcessTableProps {
  processes: Array<ProcessPerformanceSample>
  sort: SortState
  onSort: (column: SortColumn) => void
  hasMeasuredPower: boolean
}

export function ProcessTable({ processes, sort, onSort, hasMeasuredPower }: ProcessTableProps) {
  return (
    <div
      className={twMerge(
        'grid gap-x-2 border-t p-2.5',
        hasMeasuredPower
          ? 'grid-cols-[1fr_repeat(5,minmax(34px,auto))]'
          : 'grid-cols-[1fr_repeat(4,minmax(38px,auto))]',
      )}
    >
      <HeaderCell column="label" label="process" sort={sort} onSort={onSort} />
      <HeaderCell column="cpuPercent" label="cpu%" sort={sort} onSort={onSort} alignRight />
      <HeaderCell column="memoryMB" label="mb" sort={sort} onSort={onSort} alignRight />
      <HeaderCell column="idleWakeupsPerSecond" label="wake/s" sort={sort} onSort={onSort} alignRight />
      <HeaderCell column="energyImpact" label="nrg" sort={sort} onSort={onSort} alignRight />
      {hasMeasuredPower && <HeaderCell column="powerMilliwatts" label="mW" sort={sort} onSort={onSort} alignRight />}
      {sortProcesses(processes, sort)
        .slice(0, MAX_PROCESS_ROWS)
        .map((process) => (
          <div
            key={process.pid}
            className={twMerge('grid grid-cols-subgrid', hasMeasuredPower ? 'col-span-6' : 'col-span-5')}
          >
            <span className="text-foreground-secondary truncate">{process.label}</span>
            <Metric className="text-right" value={process.cpuPercent} thresholds={THRESHOLDS.cpuPercent} />
            <Metric className="text-right" value={process.memoryMB} thresholds={THRESHOLDS.memoryMB} digits={0} />
            <Metric
              className="text-right"
              value={process.idleWakeupsPerSecond}
              thresholds={THRESHOLDS.wakeups}
              digits={0}
            />
            <Metric className="text-right" value={process.energyImpact} thresholds={THRESHOLDS.energy} digits={0} />
            {hasMeasuredPower &&
              (process.powerMilliwatts !== undefined ? (
                <Metric
                  className="text-right"
                  value={process.powerMilliwatts}
                  thresholds={THRESHOLDS.powerMw}
                  digits={0}
                />
              ) : (
                <span className="text-foreground-disabled text-right">–</span>
              ))}
          </div>
        ))}
    </div>
  )
}
